import Spinner from 'components-stateless/Spinner';
import { useGetOnboardingEligibilityQuery } from 'containers/Ticketing/_api/useGetOnboardingEligibilityQuery';
import { useStore } from 'hooks/useStore';
import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';

const ONBOARDING_URL = '/app/ticketing/onboarding';
const ERROR_MESSAGE = 'Please onboard through general onboarding page first';

export const OnboardingWizardGuard = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const addNotificationError = useStore(
    (state) => state.notificationSlice.addNotificationError
  );

  const { data, isLoading, isFetching } = useGetOnboardingEligibilityQuery();

  // Toast must fire once per redirect, not on every render the redirect
  // triggers. The ref guards against React Query re-running effects when the
  // component re-renders before the navigation unmounts it.
  const hasShownToastRef = useRef(false);
  const isGeneralOnboardingComplete = data?.isGeneralOnboardingComplete;
  // `isLoading` only flags the first fetch — once any value is cached it
  // stays false even while a fresh fetch is in flight. We must wait for that
  // fresh fetch (`isFetching`) before deciding, otherwise a stale "false"
  // from a previous visit will trigger a wrongful redirect.
  const isResolving = isLoading || isFetching;
  const shouldRedirect = !isResolving && isGeneralOnboardingComplete === false;

  useEffect(() => {
    if (shouldRedirect && !hasShownToastRef.current) {
      hasShownToastRef.current = true;
      addNotificationError({ message: ERROR_MESSAGE });
    }
  }, [shouldRedirect, addNotificationError]);

  if (isResolving) {
    return <Spinner />;
  }

  if (shouldRedirect) {
    return <Navigate to={ONBOARDING_URL} replace />;
  }

  return <>{children}</>;
};
